import { z } from 'zod';

export const SearchFocusSchema = z.object({
  focusQuery: z.string().trim().min(1).optional().catch(undefined),
  focusBucketId: z.string().trim().min(1).optional().catch(undefined),
  focusEditorId: z.string().trim().min(1).optional().catch(undefined),
  focusThreadId: z.string().trim().min(1).optional().catch(undefined),
  focusOccurrence: z.coerce.number().int().min(0).optional().catch(undefined),
});

export type SearchFocus = z.infer<typeof SearchFocusSchema>;

const SearchFocusExclusiveTargetSchema = SearchFocusSchema.refine(
  (searchFocus) => !(searchFocus.focusEditorId && searchFocus.focusThreadId),
  {
    message: 'focusEditorId and focusThreadId are mutually exclusive',
    path: ['focusEditorId'],
  }
);

const SEARCH_FOCUS_QUERY_KEYS = Object.keys(SearchFocusSchema.shape) as Array<
  keyof SearchFocus
>;

type AddSearchFocusInput =
  | {
      query: string;
      bucketId?: string;
      editorId: string;
      threadId?: undefined;
      occurrence?: number;
    }
  | {
      query: string;
      bucketId?: string;
      threadId: string;
      editorId?: undefined;
      occurrence?: number;
    };

function removeSearchFocusParams(params: URLSearchParams) {
  for (const key of SEARCH_FOCUS_QUERY_KEYS) {
    params.delete(key);
  }
}

export function parseSearchFromLocation(): Record<string, unknown> {
  if (typeof window === 'undefined') return {};

  const url = new URL(window.location.href);

  if (url.hash.includes('?')) {
    const hashSearch = url.hash.slice(url.hash.indexOf('?') + 1);
    return Object.fromEntries(new URLSearchParams(hashSearch));
  }

  return Object.fromEntries(url.searchParams);
}

export function clearSearchFocusFromLocation(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);

  removeSearchFocusParams(url.searchParams);

  if (url.hash.includes('?')) {
    const queryIndex = url.hash.indexOf('?');
    const hashPath = url.hash.slice(0, queryIndex);
    const hashSearchParams = new URLSearchParams(
      url.hash.slice(queryIndex + 1)
    );

    removeSearchFocusParams(hashSearchParams);

    url.hash = hashSearchParams.toString()
      ? `${hashPath}?${hashSearchParams.toString()}`
      : hashPath;
  }

  window.history.replaceState(window.history.state, '', url.toString());
}

export function parseSearchFocus(search: unknown): SearchFocus {
  const parsedSearch = SearchFocusExclusiveTargetSchema.safeParse(search);

  if (!parsedSearch.success) {
    return {};
  }

  return parsedSearch.data;
}

export function addSearchFocus<T extends Record<string, unknown>>(
  search: T | undefined,
  focus: AddSearchFocusInput
): T & SearchFocus {
  return {
    ...(search ?? {}),
    focusQuery: focus.query,
    focusBucketId: focus.bucketId,
    focusEditorId: focus.editorId,
    focusThreadId: focus.threadId,
    focusOccurrence: focus.occurrence ?? 0,
  } as T & SearchFocus;
}

export function clearSearchFocus<T extends Record<string, unknown>>(
  search: T | undefined
): Omit<
  T,
  | 'focusQuery'
  | 'focusBucketId'
  | 'focusEditorId'
  | 'focusThreadId'
  | 'focusOccurrence'
> {
  const {
    focusQuery: _focusQuery,
    focusBucketId: _focusBucketId,
    focusEditorId: _focusEditorId,
    focusThreadId: _focusThreadId,
    focusOccurrence: _focusOccurrence,
    ...rest
  } = (search ?? {}) as T & SearchFocus;

  return rest;
}
